<!-- Top Navbar -->
<nav
  class="fixed left-0 right-0 top-0 z-[1100] h-12 bg-ink pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)] shadow-[0_2px_8px_rgba(0,0,0,0.15)]"
>
  <div class="flex h-full max-w-full items-center gap-3 px-4 max-[1023px]:px-3">
    <!-- Logo -->
    <a [routerLink]="dashboardRoute" class="flex flex-shrink-0 items-center gap-2 no-underline">
      <div class="flex h-7 w-7 items-center justify-center rounded-md bg-accent">
        <i class="fas fa-chart-line text-[0.8rem] text-black"></i>
      </div>
      <span class="whitespace-nowrap text-[15px] font-bold text-white max-[480px]:hidden"
        >Morichal Corp</span
      >
    </a>

    <!-- Desktop Navigation -->
    <div
      data-testid="desktop-nav"
      class="ml-4 flex flex-1 items-center gap-1 overflow-x-auto [scrollbar-width:none] max-lg:hidden [&::-webkit-scrollbar]:hidden"
    >
      <!-- Below 1440 px the ten items only fit as icons; from 1440 as words (BUG-039) -->
      <a
        *ngFor="let item of visibleNavItems()"
        [routerLink]="item.route"
        [title]="navLabel(item)"
        [attr.aria-label]="navLabel(item)"
        class="flex items-center gap-2 whitespace-nowrap rounded px-2.5 py-1.5 text-base font-medium no-underline transition-all duration-200 ease-in-out"
        [ngClass]="
          isRouteActive(item.route)
            ? 'bg-accent/15 text-accent [&>i]:text-accent'
            : 'text-gray-400 hover:bg-white/[0.08] hover:text-white'
        "
      >
        <i [class]="item.icon" class="w-3.5 text-center text-xs min-[1440px]:hidden"></i>
        <span class="max-[1439px]:hidden">{{ t('nav.' + item.translationKey) }}</span>
        @if (badgeFor(item); as badge) {
          <span
            aria-hidden="true"
            class="min-w-5 rounded-full bg-accent px-1.5 text-center text-xs font-semibold tabular-nums text-black"
            data-testid="nav-to-file-badge"
            >{{ badge }}</span
          >
        }
      </a>
    </div>

    <!-- Right Actions -->
    <div class="ml-auto flex flex-shrink-0 items-center gap-2">
      <!-- Notifications -->
      <button
        class="relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-[15px] transition-all duration-200 ease-in-out hover:bg-white/[0.08] hover:text-white"
        [ngClass]="hasUrgentNotifications ? 'text-danger' : 'text-gray-400'"
        [class.mr-3]="notificationCount > 0"
        (click)="navigateToNotifications()"
        [title]="t('nav.notifications')"
      >
        <i class="fas fa-bell"></i>
        <span
          *ngIf="notificationCount > 0"
          class="absolute -top-1.5 left-1/2 ml-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-danger-600 px-1 text-xs font-semibold text-white"
          [class.animate-pulse]="hasUrgentNotifications"
        >
          {{ notificationBadge }}
        </span>
      </button>

      <app-language-switcher
        class="max-lg:hidden"
        variant="dropdown"
        tone="dark"
      ></app-language-switcher>

      <!-- User Menu -->
      <div class="user-menu-container relative max-lg:hidden">
        <button
          class="flex cursor-pointer items-center gap-2 rounded-md border border-white/10 bg-transparent px-2 py-1 transition-all duration-200 ease-in-out hover:border-white/20 hover:bg-white/[0.08]"
          (click)="toggleUserMenu($event)"
        >
          <img
            src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg"
            [alt]="t('nav.userAvatar')"
            class="h-[26px] w-[26px] rounded object-cover"
          />
          <span
            class="max-w-[80px] overflow-hidden text-ellipsis whitespace-nowrap text-base font-medium text-white max-[1199px]:hidden"
            >{{ currentUser?.first_name || currentUser?.username || t('nav.user') }}</span
          >
          <i class="fas fa-chevron-down text-xs text-gray-400 max-[1199px]:hidden"></i>
        </button>

        <div
          *ngIf="showUserMenu()"
          class="absolute right-0 top-[calc(100%+0.5rem)] z-[1200] min-w-[180px] rounded-lg bg-white py-2 shadow-[0_4px_20px_rgba(0,0,0,0.15)] animate-[fadeIn_0.15s_ease]"
        >
          <a
            routerLink="/profile"
            (click)="showUserMenu.set(false)"
            class="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-2.5 text-left text-sm text-gray-700 no-underline transition-colors duration-200 ease-in-out hover:bg-gray-100 [&_i]:w-4 [&_i]:text-center [&_i]:text-sm"
          >
            <i class="fas fa-user"></i>{{ t('nav.profile') }}
          </a>
          <a
            *ngIf="isAdmin"
            routerLink="/admin/dashboard"
            (click)="showUserMenu.set(false)"
            class="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-2.5 text-left text-sm text-gray-700 no-underline transition-colors duration-200 ease-in-out hover:bg-gray-100 [&_i]:w-4 [&_i]:text-center [&_i]:text-sm"
          >
            <i class="fas fa-user-shield"></i>{{ t('nav.adminPanel') }}
          </a>
          <hr class="my-2 border-0 border-t border-gray-200" />
          <button
            (click)="logout()"
            class="flex w-full cursor-pointer items-center gap-3 border-0 bg-transparent px-4 py-2.5 text-left text-sm text-danger no-underline transition-colors duration-200 ease-in-out hover:bg-danger-50 [&_i]:w-4 [&_i]:text-center [&_i]:text-sm"
          >
            <i class="fas fa-sign-out-alt"></i>{{ t('nav.signOut') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Mobile Menu Dropdown -->
  <div
    id="phone-more-menu"
    class="fixed inset-x-0 bottom-tab-bar z-[1090] max-h-[calc(100dvh-3rem-theme(spacing.tab-bar))] overflow-y-auto rounded-t-2xl bg-ink shadow-[0_-4px_20px_rgba(0,0,0,0.2)] animate-[fadeIn_0.2s_ease] lg:hidden"
    *ngIf="isMobileMenuOpen()"
  >
    <div class="p-2">
      <a
        *ngFor="let item of visibleNavItems()"
        [routerLink]="item.route"
        [attr.aria-label]="badgeFor(item) ? navLabel(item) : null"
        class="flex items-center gap-3 rounded-lg px-4 py-3.5 text-[15px] no-underline transition-all duration-200 ease-in-out [&_i]:w-5 [&_i]:text-center"
        [ngClass]="
          isRouteActive(item.route)
            ? 'bg-accent/15 text-accent [&>i]:text-accent'
            : 'text-gray-400 hover:bg-white/[0.08] hover:text-white'
        "
        (click)="closeMobileMenu()"
      >
        <i [class]="item.icon"></i>
        <span>{{ t('nav.' + item.translationKey) }}</span>
        @if (badgeFor(item); as badge) {
          <span
            aria-hidden="true"
            class="ml-auto min-w-6 rounded-full bg-accent px-2 text-center text-sm font-semibold tabular-nums text-black"
            data-testid="nav-to-file-badge"
            >{{ badge }}</span
          >
        }
      </a>
    </div>

    <div class="mx-4 my-2 h-px bg-white/[0.08]"></div>

    <div class="px-4 py-3">
      <app-language-switcher variant="inline" tone="dark"></app-language-switcher>
    </div>

    <div class="mx-4 my-2 h-px bg-white/[0.08]"></div>

    <!-- Mobile User Section -->
    <div class="px-4 pb-4 pt-3">
      <div class="mb-3 flex items-center gap-3 rounded-lg bg-white/[0.05] p-3">
        <img
          src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg"
          [alt]="t('nav.userAvatar')"
          class="h-10 w-10 rounded-lg object-cover"
        />
        <div class="flex-1">
          <span class="block text-[15px] font-semibold text-white">{{
            currentUser?.first_name || currentUser?.username || t('nav.user')
          }}</span>
          <span class="block text-base text-gray-400">{{
            isAdmin ? t('nav.administrator') : t('nav.user')
          }}</span>
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <a
          routerLink="/profile"
          (click)="closeMobileMenu()"
          class="flex w-full cursor-pointer items-center gap-3 rounded-md border-0 bg-transparent px-4 py-3 text-left text-[15px] text-gray-400 no-underline transition-all duration-200 ease-in-out hover:bg-white/[0.08] hover:text-white [&_i]:w-5 [&_i]:text-center"
        >
          <i class="fas fa-user"></i>{{ t('nav.profile') }}
        </a>
        <a
          *ngIf="isAdmin"
          routerLink="/admin/dashboard"
          (click)="closeMobileMenu()"
          class="flex w-full cursor-pointer items-center gap-3 rounded-md border-0 bg-transparent px-4 py-3 text-left text-[15px] text-gray-400 no-underline transition-all duration-200 ease-in-out hover:bg-white/[0.08] hover:text-white [&_i]:w-5 [&_i]:text-center"
        >
          <i class="fas fa-user-shield"></i>{{ t('nav.adminPanel') }}
        </a>
        <button
          (click)="logout()"
          class="flex w-full cursor-pointer items-center gap-3 rounded-md border-0 bg-transparent px-4 py-3 text-left text-[15px] text-danger transition-all duration-200 ease-in-out hover:bg-danger/10 [&_i]:w-5 [&_i]:text-center"
        >
          <i class="fas fa-sign-out-alt"></i>{{ t('nav.signOut') }}
        </button>
      </div>
    </div>
  </div>
</nav>

<!-- Mobile Overlay -->
<div
  class="fixed inset-x-0 bottom-tab-bar top-12 z-[1080] bg-black/50 animate-[fadeIn_0.2s_ease] lg:hidden"
  *ngIf="isMobileMenuOpen()"
  (click)="closeMobileMenu()"
></div>

<!-- Phone tab bar (card 835): the main pages one tap away, "More" opens the full menu.
     Phone and desktop parts are switched by the lg breakpoint only, the same media rule as
     --tab-bar-h (styles.scss), so bar, menu and room never disagree. -->
<nav
  data-testid="phone-tab-bar"
  [attr.aria-label]="t('nav.tabBar')"
  class="fixed inset-x-0 bottom-0 z-tab-bar flex h-tab-bar border-t border-gray-200 bg-white pb-[env(safe-area-inset-bottom)] lg:hidden"
>
  @for (item of tabItems(); track item.route) {
    <a
      [routerLink]="item.route"
      routerLinkActive
      ariaCurrentWhenActive="page"
      [attr.aria-label]="badgeFor(item) ? navLabel(item) : null"
      (click)="closeMobileMenu()"
      class="relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-[13px] font-medium text-gray-600 no-underline aria-[current=page]:text-accent-700 [&[aria-current=page]>i]:text-accent"
    >
      <i [class]="item.icon" class="text-xl leading-6" aria-hidden="true"></i>
      <span class="max-w-full truncate px-1">{{ t('nav.' + item.translationKey) }}</span>
      @if (badgeFor(item); as badge) {
        <span
          aria-hidden="true"
          class="absolute left-1/2 top-1 ml-2 min-w-5 rounded-full bg-accent px-1.5 text-center text-xs font-semibold tabular-nums text-black"
          data-testid="nav-to-file-badge"
          >{{ badge }}</span
        >
      }
    </a>
  }
  <button
    type="button"
    [attr.aria-controls]="isMobileMenuOpen() ? 'phone-more-menu' : null"
    [attr.aria-expanded]="isMobileMenuOpen()"
    class="flex min-w-0 flex-1 cursor-pointer flex-col items-center justify-center gap-0.5 border-0 bg-transparent text-[13px] font-medium text-gray-600 aria-expanded:text-accent-700 [&[aria-expanded=true]>i]:text-accent"
    (click)="toggleMobileMenu()"
  >
    <i class="fas fa-ellipsis-h text-xl leading-6" aria-hidden="true"></i>
    <span>{{ t('common.more') }}</span>
  </button>
</nav>
